import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Unlock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ProjectInfo from '../components/ProjectInfo';
import PresentationImage from '../components/PresentationImage';
import MultimediaSection from '../components/MultimediaSection';
import DocumentRepository from '../components/DocumentRepository';
import FinancialDashboard from '../components/FinancialDashboard';
import TeamAndPartners from '../components/TeamAndPartners';
import InvestorInquiry from '../components/InvestorInquiry';
import ContactForm from '../components/ContactForm';
import ChatAssistant from '../components/ChatAssistant';
import AdminLoginModal from '../components/AdminLoginModal';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import Footer from '../components/Footer';
import CookieConsent from '../components/CookieConsent';
import { projectsAPI, documentsAPI, imagesAPI, financialAPI } from '../services/api';

const HomePage = () => {
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [project, setProject] = useState({
    title: 'EvoCore PACA : L\'IA pour les Auto-Entrepreneurs',
    description: 'EvoCore PACA accompagne les indépendants et auto-entrepreneurs de la région Sud dans leur transformation numérique. En 2025, découvrez comment Evocore IA Organique révolutionne la relation client grâce à l\'IA cognitive — orientée vers la réflexion et l\'analyse — et au marketing automation. Oubliez les chatbots classiques : nos nouvelles plateformes comprennent le contexte, anticipent vos intentions et personnalisent chaque échange.',
    url: 'https://evocore-saas1.vercel.app',
    youtube_url: 'https://www.youtube.com/embed/jV1vkHv4zq8',
    presentation_image: 'https://picsum.photos/seed/evocore-paca/1024/576',
  });
  const [documents, setDocuments] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const checkAdminStatus = useCallback(() => {
    const token = localStorage.getItem('token');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (token && isAdmin) {
      setIsAdminMode(true);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      // Load project data
      const projectsRes = await projectsAPI.getAll();
      if (projectsRes.data) {
        // Si c'est un tableau, prendre le premier élément
        if (Array.isArray(projectsRes.data) && projectsRes.data.length > 0) {
          setProject(projectsRes.data[0]);
        } else if (!Array.isArray(projectsRes.data)) {
          // Si c'est un objet unique, l'utiliser directement
          setProject(projectsRes.data);
        }
      }

      // Load documents
      const docsRes = await documentsAPI.getAll();
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);

      // Load gallery images
      const imagesRes = await imagesAPI.getAll();
      setGalleryImages(Array.isArray(imagesRes.data) ? imagesRes.data : []);
    } catch (error) {
      console.error('Error loading data:', error);
      // Définir des valeurs par défaut en cas d'erreur
      setProject({
        title: 'EvoCore PACA : L\'IA pour les Auto-Entrepreneurs',
        description: 'EvoCore PACA accompagne les indépendants et auto-entrepreneurs de la région Sud dans leur transformation numérique. En 2025, découvrez comment Evocore IA Organique révolutionne la relation client grâce à l\'IA cognitive — orientée vers la réflexion et l\'analyse — et au marketing automation. Oubliez les chatbots classiques : nos nouvelles plateformes comprennent le contexte, anticipent vos intentions et personnalisent chaque échange.'
      });
      setDocuments([]);
      setGalleryImages([]);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
    checkAdminStatus();
  }, [loadInitialData, checkAdminStatus]);

  const handleAdminToggle = () => {
    if (isAdminMode) {
      // Logout
      localStorage.removeItem('token');
      localStorage.removeItem('isAdmin');
      setIsAdminMode(false);
      toast.success('Déconnecté du mode admin');
    } else {
      // Show login modal
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (userData) => {
    setIsAdminMode(true);
    setShowLoginModal(false);
    toast.success('Connecté en mode admin');
  };

  const handleDocumentUpload = async (files) => {
    if (!isAdminMode) return;
    
    try {
      const uploadPromises = Array.from(files).map(file => 
        documentsAPI.upload(file, 'both')
      );
      const results = await Promise.all(uploadPromises);
      setDocuments([...results.map(r => r.data), ...documents]);
      toast.success(`${files.length} document(s) ajouté(s)`);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDocumentDelete = async (docId) => {
    if (!isAdminMode) return;
    
    try {
      await documentsAPI.delete(docId);
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleImageUpload = async (files) => {
    if (!isAdminMode) return;
    
    try {
      const uploadPromises = Array.from(files).map(file => 
        imagesAPI.upload(file)
      );
      const results = await Promise.all(uploadPromises);
      setGalleryImages([...galleryImages, ...results.map(r => r.data)]);
      toast.success(`${files.length} image(s) ajoutée(s)`);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleImageDelete = async (imageId) => {
    if (!isAdminMode) return;
    
    try {
      await imagesAPI.delete(imageId);
      setGalleryImages(galleryImages.filter(img => img.id !== imageId));
      toast.success('Image supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <>
      <SEO 
        title={`${project.title} | EvoCore PACA`}
        description={project.description}
        keywords="IA, intelligence artificielle, auto-entrepreneurs, PACA, Côte d'Azur, Nice, Cannes, Monaco, transformation numérique, French Tech, innovation"
        ogImage={project.presentation_image}
        url={typeof window !== 'undefined' ? window.location.href : project.url}
      />
      <StructuredData project={project} />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="min-h-screen bg-black/20">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <header className="text-center mb-8 md:mb-12 relative animate-fadeIn" role="banner">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-8 h-8 text-blue-400" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                {project.title}
              </h1>
            </div>
            <p className="mt-2 text-lg text-gray-300">Votre partenaire numérique en région Sud</p>
            
            {/* Admin Toggle */}
            <div className="absolute top-0 right-0">
              <button
                data-testid="admin-toggle-btn"
                onClick={handleAdminToggle}
                className="p-3 rounded-full bg-slate-800/50 border border-slate-700 hover:bg-slate-700/50 text-gray-300 hover:text-white transition-all duration-300 backdrop-blur-sm"
                aria-label={isAdminMode ? 'Quitter le mode admin' : 'Activer le mode admin'}
                title={isAdminMode ? 'Quitter le mode admin' : 'Activer le mode admin'}
              >
                {isAdminMode ? (
                  <Unlock className="w-6 h-6 text-green-400" />
                ) : (
                  <Lock className="w-6 h-6" />
                )}
              </button>
            </div>
          </header>

          {/* Main Content */}
          <main className="grid grid-cols-1 lg:grid-cols-3 gap-6" role="main">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              <PresentationImage 
                imageUrl={project.presentation_image} 
                isLoading={isLoading}
                data-testid="presentation-image"
              />
              <ProjectInfo
                project={project}
                isAdmin={isAdminMode}
                onUpdate={(updatedProject) => setProject(updatedProject)}
                data-testid="project-info"
              />
              <FinancialDashboard 
                isAdmin={isAdminMode}
                data-testid="financial-dashboard"
              />
              <MultimediaSection
                youtubeUrl={project.youtube_url}
                galleryImages={galleryImages}
                isAdmin={isAdminMode}
                onImageUpload={handleImageUpload}
                onImageDelete={handleImageDelete}
                onYoutubeUpdate={async (newUrl) => {
                  if (project.id) {
                    try {
                      await projectsAPI.update(project.id, { youtube_url: newUrl });
                      setProject({ ...project, youtube_url: newUrl });
                      toast.success('URL YouTube mise à jour');
                    } catch (error) {
                      toast.error('Erreur lors de la mise à jour');
                    }
                  }
                }}
                data-testid="multimedia-section"
              />
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1 space-y-6">
              <DocumentRepository
                documents={documents}
                isAdmin={isAdminMode}
                onUpload={handleDocumentUpload}
                onDelete={handleDocumentDelete}
                data-testid="document-repository"
              />
              <TeamAndPartners 
                isAdmin={isAdminMode}
                data-testid="team-partners"
              />
              <InvestorInquiry data-testid="investor-inquiry" />
              <ContactForm data-testid="contact-form" />
            </div>
          </main>

          {/* Footer */}
          <Footer />

          {/* Chat Assistant */}
          <ChatAssistant data-testid="chat-assistant" />

          {/* Cookie Consent */}
          <CookieConsent />

          {/* Admin Login Modal */}
          <AdminLoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onLoginSuccess={handleLoginSuccess}
          />
        </div>
      </div>
      </div>
    </>
  );
};

export default HomePage;
